// Shared by world.ts and levels.ts
import type { ObjData } from '../../core/types';

export const DEG = Math.PI / 180, K = 'pizza';
export const TP = 3.8;                         // radius per tooth → same tooth size on every wheel
export const NS = 10, NB = 16;                  // teeth: small / big wheel
export const RS = NS * TP, RB = NB * TP;        // 38 / 60.8
export const RP = 20;                           // belt roller radius

/** A point [x, y] */
export type Pt = [number, number];

// ---------- object data ----------
/** Pegs on the wall where gears can go: { t: 'pizza_pegs', p: [[x, y], ...] } */
export interface PegsObj extends ObjData { p: Pt[] }
/** Fixed gear (motor: 1 = clockwise, -1 = anticlockwise, 0 = loose wheel). s: 's' | 'b' | 'sb' */
export interface GearObj extends ObjData { motor?: number; s?: string; speed?: number; ph?: number }
/** Conveyor belt: driven from the left (L) or right (R) end; gear: the belt's own gear ('s' | 'b' | 'sb'), none = a peg; k = speed factor */
export interface BeltObj extends ObjData { x1: number; x2: number; drive: 'L' | 'R'; gear?: string; gear2?: string; k?: number }
/** Dough roller: r = radius, gear: own gear (none = a peg), k = speed factor */
export interface RollerObj extends ObjData { r?: number; gear?: string; k?: number }

// Pegs and belts have no single x/y: their level-data fields (instead of { x, y })
declare module '../../core/types' {
  interface ObjDataTypes {
    pizza_pegs: { p: Pt[] };
    pizza_belt: { x1: number; x2: number; y: number; drive: 'L' | 'R'; gear?: string; gear2?: string; k?: number };
  }
}

// ---------- geometry helpers for the levels ----------
export const at = (p: Pt, r1: number, r2: number, deg: number): Pt => [Math.round(p[0] + (r1 + r2) * Math.cos(deg * DEG)), Math.round(p[1] + (r1 + r2) * Math.sin(deg * DEG))];
// point at distance d1 from P and d2 from Q (side = 1 / -1)
export function meet(P: Pt, d1: number, Q: Pt, d2: number, side: number): Pt {
  const dx = Q[0] - P[0], dy = Q[1] - P[1], d = Math.hypot(dx, dy);
  const a = (d1 * d1 - d2 * d2 + d * d) / (2 * d), h = Math.sqrt(Math.max(0, d1 * d1 - a * a));
  const mx = P[0] + a * dx / d, my = P[1] + a * dy / d;
  return [Math.round(mx - side * h * dy / d), Math.round(my + side * h * dx / d)];
}
// the meeting point furthest from `away`
export const meetFar = (P: Pt, d1: number, Q: Pt, d2: number, away: Pt): Pt => { const a = meet(P, d1, Q, d2, 1), b = meet(P, d1, Q, d2, -1); return Math.hypot(a[0] - away[0], a[1] - away[1]) > Math.hypot(b[0] - away[0], b[1] - away[1]) ? a : b; };
// a tomato lying on a belt at x = bx
export const onBelt = (bx: number, beltY: number) => ({ x: bx, y: beltY - RP - 16, r: 15 });
